"use client";
import * as React from "react";
import { useState } from "react";
import { X, Upload, CheckCircle, AlertCircle, User, Mail, Phone, MessageSquare, FileText } from "lucide-react";
 
function ApplicationForm({ job, onClose }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    selectedJob: job.title,
    aboutYourself: "",
    resume: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [debugInfo, setDebugInfo] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
 
  // Google Apps Script web app URL
  const GOOGLE_SCRIPT_URL =
    "https://script.google.com/macros/s/AKfycbwhnmhrnCjaX2fqY0DXGQSW1jkUhPIivyzdkbGrzcHGovmDhDrYKIg98UK3eiOZv2jM/exec";
 
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (submitStatus) { setSubmitStatus(null); setDebugInfo(""); }
  };
 
  // Convert file to base64 string (without the data-URL prefix)
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = (err) => reject(err);
    });
 
  const validateAndSetFile = (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert("File size must be less than 2 MB."); return; }
    const allowed = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    if (!allowed.includes(file.type)) { alert("Please upload a PDF or DOC file."); return; }
    setFormData((prev) => ({ ...prev, resume: file }));
    if (submitStatus) { setSubmitStatus(null); setDebugInfo(""); }
  };
 
  const handleFileChange = (e) => validateAndSetFile(e.target.files[0]);
 
  const handleDrag = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };
 
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) validateAndSetFile(e.dataTransfer.files[0]);
  };
 
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setDebugInfo("");
    setUploadProgress(0);
 
    try {
      // ── Step 1: convert resume to base64 ──────────────────────────────────
      setUploadProgress(20);
      setDebugInfo("Reading resume file…");
 
      let resumeBase64 = "";
      if (formData.resume) {
        resumeBase64 = await fileToBase64(formData.resume);
      }
 
      // ── Step 2: build ONE payload that includes everything ─────────────────
      setUploadProgress(40);
      setDebugInfo("Preparing payload…");
 
      const payload = new URLSearchParams();
      payload.append("formType",       "jobApplication");
      payload.append("name",           formData.name);
      payload.append("email",          formData.email);
      payload.append("phone",          formData.phone);
      payload.append("selectedJob",    formData.selectedJob);
      payload.append("aboutYourself",  formData.aboutYourself);
      payload.append("timestamp",      new Date().toISOString());
 
      if (formData.resume) {
        payload.append("resumeBase64",    resumeBase64);
        payload.append("resumeFileName",  formData.resume.name);
        payload.append("resumeType",      formData.resume.type);
        payload.append("resumeSize",      String(formData.resume.size));
      }
 
      // ── Step 3: single fetch call ──────────────────────────────────────────
      setUploadProgress(60);
      setDebugInfo("Submitting application + uploading resume…");
 
      // Replace the fetch call with this:
await fetch(GOOGLE_SCRIPT_URL, {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    formType:      "jobApplication",
    name:          formData.name,
    email:         formData.email,
    phone:         formData.phone,
    selectedJob:   formData.selectedJob,
    aboutYourself: formData.aboutYourself,
    timestamp:     new Date().toISOString(),
    resumeBase64:  resumeBase64,
    resumeFileName: formData.resume?.name || "",
    resumeType:    formData.resume?.type || "",
    resumeSize:    String(formData.resume?.size || 0),
  }),
  mode: "no-cors",
});
 
      setUploadProgress(100);
      setSubmitStatus("success");
      setDebugInfo(`Submitted at ${new Date().toLocaleTimeString()}`);
 
      setFormData({
        name: "", email: "", phone: "",
        selectedJob: job.title, aboutYourself: "", resume: null,
      });
 
      setTimeout(() => onClose(), 3000);
 
    } catch (error) {
      console.error("Submission error:", error);
      setSubmitStatus("error");
      setDebugInfo(`Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };
 
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto custom-scrollbar">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-[#1E293B]">Apply for Position</h2>
            <p className="text-[#64748B] mt-1">{job.title}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200" aria-label="Close">
            <X className="w-6 h-6 text-[#64748B]" />
          </button>
        </div>
 
        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
 
          {/* Success */}
          {submitStatus === "success" && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3 text-green-800">
              <CheckCircle size={20} />
              <div>
                <div className="font-medium">Application submitted successfully!</div>
                <div className="text-sm text-green-600">We'll review your application and get back to you soon.</div>
                {debugInfo && <div className="text-xs text-green-500 mt-1">{debugInfo}</div>}
              </div>
            </div>
          )}
 
          {/* Error */}
          {submitStatus === "error" && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-800">
              <AlertCircle size={20} />
              <div>
                <div className="font-medium">Failed to submit application</div>
                <div className="text-sm text-red-600">Please try again or contact us at career@vastainfo.com</div>
                {debugInfo && <div className="text-xs text-red-500 mt-1">{debugInfo}</div>}
              </div>
            </div>
          )}
 
          {/* Progress */}
          {isSubmitting && uploadProgress > 0 && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-800">Submitting Application</span>
                <span className="text-sm text-blue-600">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
              </div>
              {debugInfo && <div className="text-xs text-blue-600 mt-2">{debugInfo}</div>}
            </div>
          )}
 
          {/* Idle debug info */}
          {debugInfo && !isSubmitting && submitStatus === null && (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="text-xs text-gray-600">{debugInfo}</div>
            </div>
          )}
 
          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-[#1E293B] mb-2">Full Name *</label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]"><User size={18} /></div>
              <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} required disabled={isSubmitting}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1719A8] focus:border-transparent transition-all duration-300 disabled:opacity-50"
                placeholder="Enter your full name" />
            </div>
          </div>
 
          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-[#1E293B] mb-2">Email Address *</label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]"><Mail size={18} /></div>
              <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} required disabled={isSubmitting}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1719A8] focus:border-transparent transition-all duration-300 disabled:opacity-50"
                placeholder="Enter your email address" />
            </div>
          </div>
 
          {/* Phone */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-[#1E293B] mb-2">Phone Number *</label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]"><Phone size={18} /></div>
              <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} required disabled={isSubmitting}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1719A8] focus:border-transparent transition-all duration-300 disabled:opacity-50"
                placeholder="Enter your phone number" />
            </div>
          </div>
 
          {/* Job (read-only) */}
          <div>
            <label htmlFor="selectedJob" className="block text-sm font-medium text-[#1E293B] mb-2">Position Applied For</label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]"><MessageSquare size={18} /></div>
              <input type="text" id="selectedJob" name="selectedJob" value={formData.selectedJob} readOnly
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl bg-gray-50 text-[#64748B]" />
            </div>
          </div>
 
          {/* About */}
          <div>
            <label htmlFor="aboutYourself" className="block text-sm font-medium text-[#1E293B] mb-2">Tell us about yourself *</label>
            <textarea id="aboutYourself" name="aboutYourself" value={formData.aboutYourself} onChange={handleInputChange}
              required rows={4} disabled={isSubmitting}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1719A8] focus:border-transparent transition-all duration-300 resize-none disabled:opacity-50"
              placeholder="Tell us about your experience, skills, and why you're interested in this position…" />
          </div>
 
          {/* Resume Upload */}
          <div>
            <label className="block text-sm font-medium text-[#1E293B] mb-2">Resume Upload * (PDF or DOC, max 2 MB)</label>
            <div
              className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all duration-300 ${
                dragActive ? "border-[#1719A8] bg-blue-50"
                : formData.resume ? "border-green-400 bg-green-50"
                : "border-gray-300 hover:border-[#1719A8] hover:bg-blue-50"
              }`}
              onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
            >
              <input type="file" accept=".pdf,.doc,.docx" onChange={handleFileChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" disabled={isSubmitting} />
 
              {formData.resume ? (
                <div className="flex items-center justify-center space-x-2 text-green-600">
                  <FileText size={24} />
                  <div>
                    <div className="font-medium">{formData.resume.name}</div>
                    <div className="text-sm text-green-500">{(formData.resume.size / 1024 / 1024).toFixed(2)} MB</div>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload size={32} className="mx-auto text-[#64748B]" />
                  <div className="text-[#64748B]"><span className="font-medium">Click to upload</span> or drag and drop</div>
                  <div className="text-sm text-[#64748B]">PDF or DOC files only (max 2 MB)</div>
                </div>
              )}
            </div>
            <div className="mt-2 text-xs text-[#64748B]">
              Your resume will be uploaded to Google Drive and a viewable link saved in our records.
            </div>
          </div>
 
          {/* Buttons */}
          <div className="flex gap-4 pt-4">
            <button type="button" onClick={onClose} disabled={isSubmitting}
              className="flex-1 px-6 py-3 border border-gray-300 text-[#64748B] font-semibold rounded-xl hover:bg-gray-50 transition-colors duration-300 disabled:opacity-50">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting || !formData.resume}
              className={`flex-1 px-6 py-3 font-semibold rounded-xl transition-all duration-300 ${
                isSubmitting || !formData.resume
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-gradient-to-r from-[#1719A8] to-[#3B82F6] text-white hover:from-[#0F1280] hover:to-[#1719A8] transform hover:scale-105 shadow-lg"
              }`}>
              {isSubmitting ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Submitting…
                </div>
              ) : "Submit Application"}
            </button>
          </div>
 
          <p className="mt-4 text-center text-[#64748B] text-xs">
            ⚡ Powered by Google Sheets &amp; Drive integration
          </p>
        </form>
      </div>
    </div>
  );
}
 
export default ApplicationForm;